import type { ComponentPublicInstance } from 'vue';
import { captureException } from '@sentry/vue';
import { buildErrorReport } from '@lib/error-report';
import type { ErrorProps } from '@pages/error.vue';

/** What the error modal (pages/error.vue) shows. */
export interface ErrorData {
  title?: string;
  message?: string;
  report?: string;
  stackTrace?: string;
  mode?: ErrorProps['mode'];
  fixed?: boolean;
}

interface ApiClientError extends Error {
  config?: { url?: string; method?: string };
  response?: {
    status?: number;
    data: {
      message?: string;
      title?: string;
      errorCode?: string;
      correlationId?: string;
      /** Exception chain — for the copyable report only, never displayed. */
      technicalDetail?: string;
      /** Only sent by a Development backend. */
      stackTrace?: string;
    };
    config?: { url?: string; method?: string };
  };
}

/** Who and where, for the report: the PLM names the brand, the supplier portal the company. */
export interface ErrorContext {
  user?: string;
  brand?: string;
}

const isApiClientError = (err: unknown): err is ApiClientError =>
  typeof err === 'object' &&
  err !== null &&
  'response' in err &&
  typeof (err as ApiClientError).response === 'object' &&
  typeof (err as ApiClientError).response?.data === 'object';

const devStackTrace = (data?: { stackTrace?: string }) =>
  process.env.NODE_ENV === 'development' ? data?.stackTrace : undefined;

// A copyable technical report for the error modal: enough context for support/devs to find
// the exact backend event, and still useful when there is no response body at all
// (network errors, gateway 502s).
export function errorReportFor(
  err: unknown,
  context: ErrorContext,
  data?: NonNullable<ApiClientError['response']>['data'],
  sentryEventId?: string
) {
  const axiosError = err as ApiClientError;
  const request = axiosError?.config ?? axiosError?.response?.config;

  return buildErrorReport({
    message: data?.technicalDetail ?? data?.message ?? (err instanceof Error ? err.message : undefined),
    errorCode: data?.errorCode,
    correlationId: data?.correlationId,
    status: axiosError?.response?.status,
    method: request?.method,
    endpoint: request?.url,
    sentryEventId,
    user: context.user,
    brand: context.brand,
    page: window.location.href,
    appVersion: import.meta.env.VITE_RELEASE_TAG,
    stackTrace: devStackTrace(data),
  });
}

/**
 * What to show for an error a component let escape, for an app's root onErrorCaptured; null for one
 * to ignore. Reports it to Sentry in production.
 */
export function errorForCaptured(
  err: Error,
  instance: ComponentPublicInstance | null,
  info: string,
  context: ErrorContext
): ErrorData | null {
  console.error(err);
  const defaultTitle = 'Oops, we ran into an issue';
  const isUserFacingError = err.name === 'ClientError';
  let sentryEventId: string | undefined;

  // Ignore errors from third-party scripts (Sentry Replay, Intercom, etc.)
  if (err?.message?.includes('Storage.getStorageKey') || !instance) return null;

  // When in production, report all errors to Sentry
  if (process.env.NODE_ENV === 'production') {
    sentryEventId = captureException(err, {
      contexts: { vueInfo: { info } },
      extra: { vueComponentName: instance?.$options.name },
    });
  }

  if (isApiClientError(err)) {
    return {
      ...err.response?.data,
      title: defaultTitle,
      stackTrace: devStackTrace(err.response?.data),
      report: errorReportFor(err, context, err.response?.data, sentryEventId),
    };
  }

  // Our own user-facing client side error, so we should show the message
  if (isUserFacingError) {
    return {
      title: defaultTitle,
      message: err.message,
      report: errorReportFor(err, context, undefined, sentryEventId),
    };
  }

  // Fallback for all other errors with no message, i.e. programming errors and unexpected issues
  return { title: defaultTitle, mode: 'refresh', report: errorReportFor(err, context, undefined, sentryEventId) };
}

/**
 * A deploy changes the bundle's hashes and old bundles are not kept, so a lazily loaded route can
 * fail to load. True for those errors: the page is reloaded instead of showing one.
 * https://vitejs.dev/guide/build#load-error-handling
 */
export function isStaleBundleError(error: Error) {
  return (
    error.message.includes('Failed to fetch dynamically imported module') || // Chrome/Edge
    error.message.includes('error loading dynamically imported module') || // Firefox
    error.message.includes('Unable to preload CSS') || // Firefox & Safari
    error.message.includes('Importing a module script failed') || // Safari
    error.message.includes('Load failed') // Safari
  );
}

/** Sentry's ignore list for the same errors, which the reload handles. */
export const staleBundleErrorMessages = [
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Unable to preload CSS',
  'Importing a module script failed',
  'Load failed',
];
